import React, { useEffect, useMemo, useState } from 'react';
import ProductList from '../components/Product/Body/ProductList';
import { Box, Typography, Tabs, Tab } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SEO from '../components/SEO';

const SITE_URL = 'https://holywearhouse.com';

export default function LikedPage() {
  const [likedProducts, setLikedProducts] = useState([]);
  const [trackedProducts, setTrackedProducts] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    setLikedProducts(JSON.parse(localStorage.getItem('likedItems')) || []);
    setTrackedProducts(JSON.parse(localStorage.getItem('trackedItems')) || []);
  }, []);

  let productsToShow = [];
  if (activeTab === 0) {
    productsToShow = likedProducts;
  } else if (activeTab === 1) {
    productsToShow = trackedProducts;
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const savedStructuredData = useMemo(() => {
    const combined = [...likedProducts, ...trackedProducts];

    const itemList = combined.length
      ? {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'Saved Christian Apparel | Holy Wear House',
          itemListElement: combined.slice(0, 20).map((product, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: product.title,
            url: product.link || SITE_URL,
            image: product.imageUrl || product.images?.[0],
            brand: product.brand,
            offers: product.price
              ? {
                  '@type': 'Offer',
                  priceCurrency: 'USD',
                  price: product.price,
                  availability: 'https://schema.org/InStock',
                }
              : undefined,
          })),
        }
      : null;

    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: `${SITE_URL}/`,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Saved Items',
          item: `${SITE_URL}/liked`,
        },
      ],
    };

    return [itemList, breadcrumb].filter(Boolean);
  }, [likedProducts, trackedProducts]);

  return (
    <Box sx={{ pb: 10 }}>
      <SEO
        title="My Saved Christian Apparel | Holy Wear House"
        description="View your loved items and price-tracked Christian clothing picks on Holy Wear House. Revisit your favorite faith-based styles in one place."
        keywords="saved christian clothing, liked christian apparel, holy wear house favorites"
        path="/liked"
        structuredData={savedStructuredData}
      />
      <Box sx={{ p: 2, pt: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
          My Profile
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mx: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 'bold',
            },
          }}
        >
          <Tab 
            icon={<FavoriteIcon />} 
            label="Loved Items" 
            iconPosition="start"
            sx={{ gap: 1 }}
          />
          <Tab 
            icon={<VisibilityIcon />} 
            label="Price Tracking" 
            iconPosition="start"
            sx={{ gap: 1 }}
          />
        </Tabs>
      </Box>

      <Box sx={{ mt: 2 }}>
        {productsToShow.length > 0 ? (
          <ProductList products={productsToShow} />
        ) : (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '50vh',
            px: 4 
          }}>
            {activeTab === 0 ? (
              <>
                <FavoriteIcon sx={{ fontSize: 60, color: 'gray', mb: 2 }} />
                <Typography variant="h6" sx={{ textAlign: 'center', color: 'gray' }}>
                  No loved items yet
                </Typography>
                <Typography variant="body2" sx={{ textAlign: 'center', color: 'gray', mt: 1 }}>
                  Start browsing and tap the heart icon on items you love!
                </Typography>
              </>
            ) : (
              <>
                <VisibilityIcon sx={{ fontSize: 60, color: 'gray', mb: 2 }} />
                <Typography variant="h6" sx={{ textAlign: 'center', color: 'gray' }}>
                  No items being tracked
                </Typography>
                <Typography variant="body2" sx={{ textAlign: 'center', color: 'gray', mt: 1 }}>
                  Tap the eye icon on any item to get notified when prices drop!
                </Typography>
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
